import { useCallback, useMemo, useState } from "react";
import { isValid, parseISO } from "date-fns";
import classNames from "classnames";
import {
    DocumentTaxonomyExtraction,
    ExtractionStatus,
} from "@common/types/financialDocument";
import { DocumentCategory } from "@common/types/documentCategory";
import { FinancialDocumentPreview } from "@components/documents/FinancialDocumentDetails/FinancialDocumentPreview";
import { DocumentCategoryCombobox } from "@components/documents/DocumentCategorySelect/DocumentCategoryCombobox";
import { DatePicker } from "@design-system/DatePicker/DatePicker";
import { Input } from "@design-system/Input/Input";
import {
    useUpdateTaxonomyExtractionFieldsMutation,
    useUpdateDocumentCategoryForTaskMutation,
} from "@api/financialDocument.api";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import type { DocWithTaxonomy } from "./documentRetrievalHelpers";
import type { DocVerificationField } from "./documentVerificationStatus";
import styles from "./DocumentRetrievalTaskBody.module.scss";

interface TaxonomyReviewDrawerContentProps {
    documentId: number;
    fileName: string;
    taxonomyExtractionStatus: ExtractionStatus;
    taxonomyExtraction: DocumentTaxonomyExtraction | null;
    documentCategory: DocWithTaxonomy["documentCategory"] | null;
    invalidFields?: ReadonlySet<DocVerificationField>;
    onPeriodStartDateChange: (value: string) => void;
    onPeriodEndDateChange: (value: string) => void;
}

interface EditableFieldProps {
    label: string;
    value: string;
    onChange: (value: string) => void;
    onBlur: () => void;
    readOnly?: boolean;
    invalid?: boolean;
}

const EditableField = ({
    label,
    value,
    onChange,
    onBlur,
    readOnly,
    invalid,
}: EditableFieldProps) => (
    <div className={styles.documentInfoField}>
        <label className={styles.documentInfoLabel}>{label}</label>
        <div className={styles.documentInfoInput}>
            <Input
                size="lg"
                value={value}
                onChange={(event) => onChange(event.target.value)}
                onBlur={onBlur}
                onKeyDown={(event) => {
                    if (event.key === "Enter") {
                        event.currentTarget.blur();
                    }
                }}
                readOnly={readOnly}
                invalid={invalid}
            />
        </div>
    </div>
);

export const TaxonomyReviewDrawerContent = ({
    documentId,
    fileName,
    taxonomyExtractionStatus,
    taxonomyExtraction,
    documentCategory,
    invalidFields,
    onPeriodStartDateChange,
    onPeriodEndDateChange,
}: TaxonomyReviewDrawerContentProps) => {
    const { isViewOnly, refuseIfViewOnly } = useViewOnlyRefusal();
    const isExtracting =
        taxonomyExtractionStatus === ExtractionStatus.IN_PROGRESS;
    const isDisabled = isExtracting || isViewOnly;
    const isPeriodInvalid = invalidFields?.has("period") ?? false;
    const isAccountInvalid = invalidFields?.has("account") ?? false;
    const isCategoryInvalid = invalidFields?.has("category") ?? false;

    const updateCategoryMutation =
        useUpdateDocumentCategoryForTaskMutation(documentId);

    const [periodStartDate, setPeriodStartDate] = useState(
        taxonomyExtraction?.periodStartDate ?? "",
    );
    const [periodEndDate, setPeriodEndDate] = useState(
        taxonomyExtraction?.periodEndDate ?? "",
    );

    const [accountNumber, setAccountNumber] = useState(
        taxonomyExtraction?.accountNumber ?? "",
    );
    const [institutionName, setInstitutionName] = useState(
        taxonomyExtraction?.institutionName ?? "",
    );

    const { mutate: updateTaxonomyFields } =
        useUpdateTaxonomyExtractionFieldsMutation(documentId);

    const saveFields = useCallback(
        (overrides: Partial<DocumentTaxonomyExtraction>) => {
            if (refuseIfViewOnly()) {
                return;
            }
            const current: DocumentTaxonomyExtraction = {
                periodStartDate: periodStartDate || null,
                periodEndDate: periodEndDate || null,
                accountNumber: accountNumber || null,
                institutionName: institutionName || null,
                otherAccountIdentifiers:
                    taxonomyExtraction?.otherAccountIdentifiers ?? null,
                ...overrides,
            };
            updateTaxonomyFields(current);
        },
        [
            periodStartDate,
            periodEndDate,
            accountNumber,
            institutionName,
            taxonomyExtraction?.otherAccountIdentifiers,
            updateTaxonomyFields,
            refuseIfViewOnly,
        ],
    );

    const handlePeriodStartDateChange = useCallback(
        (value: string | undefined) => {
            const newValue = value ?? "";
            setPeriodStartDate(newValue);
            onPeriodStartDateChange(newValue);
            if (
                (newValue || null) !==
                (taxonomyExtraction?.periodStartDate ?? null)
            ) {
                saveFields({ periodStartDate: newValue || null });
            }
        },
        [
            onPeriodStartDateChange,
            saveFields,
            taxonomyExtraction?.periodStartDate,
        ],
    );

    const handlePeriodEndDateChange = useCallback(
        (value: string | undefined) => {
            const newValue = value ?? "";
            setPeriodEndDate(newValue);
            onPeriodEndDateChange(newValue);
            if (
                (newValue || null) !==
                (taxonomyExtraction?.periodEndDate ?? null)
            ) {
                saveFields({ periodEndDate: newValue || null });
            }
        },
        [onPeriodEndDateChange, saveFields, taxonomyExtraction?.periodEndDate],
    );

    const startMaxDate = useMemo(() => {
        if (!periodEndDate) {
            return undefined;
        }
        return isValid(parseISO(periodEndDate)) ? periodEndDate : undefined;
    }, [periodEndDate]);

    const endMinDate = useMemo(() => {
        if (!periodStartDate) {
            return undefined;
        }
        return isValid(parseISO(periodStartDate)) ? periodStartDate : undefined;
    }, [periodStartDate]);

    const handleAccountNumberBlur = useCallback(() => {
        const newValue = accountNumber || null;
        if (newValue !== (taxonomyExtraction?.accountNumber ?? null)) {
            saveFields({ accountNumber: newValue });
        }
    }, [accountNumber, taxonomyExtraction?.accountNumber, saveFields]);

    const handleInstitutionNameBlur = useCallback(() => {
        const newValue = institutionName || null;
        if (newValue !== (taxonomyExtraction?.institutionName ?? null)) {
            saveFields({ institutionName: newValue });
        }
    }, [institutionName, taxonomyExtraction?.institutionName, saveFields]);

    const handleCategoryChange = useCallback(
        (category: DocumentCategory | null) => {
            if (refuseIfViewOnly()) {
                return;
            }
            updateCategoryMutation.mutate(category?.id ?? null);
        },
        [updateCategoryMutation, refuseIfViewOnly],
    );

    return (
        <div className={styles.drawerBody}>
            <section className={styles.documentInfoSummary}>
                <div className={styles.documentInfoField}>
                    <label className={styles.documentInfoLabel}>
                        Period start
                    </label>
                    <div
                        className={classNames(styles.documentInfoInput, {
                            [styles.invalidField]: isPeriodInvalid,
                        })}
                    >
                        <DatePicker
                            mode="day"
                            variant="panel-input"
                            clearable
                            placeholder="Select date"
                            value={periodStartDate || undefined}
                            onValueChange={handlePeriodStartDateChange}
                            disabled={isDisabled}
                            maxDate={startMaxDate}
                            className={styles.documentInfoDatePicker}
                            buttonProps={{
                                size: "lg",
                                block: true,
                            }}
                        />
                    </div>
                </div>
                <div className={styles.documentInfoField}>
                    <label className={styles.documentInfoLabel}>
                        Period end
                    </label>
                    <div
                        className={classNames(styles.documentInfoInput, {
                            [styles.invalidField]: isPeriodInvalid,
                        })}
                    >
                        <DatePicker
                            mode="day"
                            variant="panel-input"
                            clearable
                            placeholder="Select date"
                            value={periodEndDate || undefined}
                            onValueChange={handlePeriodEndDateChange}
                            disabled={isDisabled}
                            minDate={endMinDate}
                            className={styles.documentInfoDatePicker}
                            buttonProps={{
                                size: "lg",
                                block: true,
                            }}
                        />
                    </div>
                </div>
                <EditableField
                    label="Account #"
                    value={accountNumber}
                    onChange={setAccountNumber}
                    onBlur={handleAccountNumberBlur}
                    readOnly={isDisabled}
                    invalid={isAccountInvalid}
                />
                <EditableField
                    label="Institution"
                    value={institutionName}
                    onChange={setInstitutionName}
                    onBlur={handleInstitutionNameBlur}
                    readOnly={isDisabled}
                />
                <div className={styles.documentInfoField}>
                    <label className={styles.documentInfoLabel}>Category</label>
                    <div
                        className={classNames(styles.documentInfoInput, {
                            [styles.invalidField]: isCategoryInvalid,
                        })}
                    >
                        <DocumentCategoryCombobox
                            selectedCategory={documentCategory ?? null}
                            onChange={handleCategoryChange}
                            readOnly={isDisabled}
                            hideFooter
                            triggerButtonProps={{
                                variant: "primary",
                                block: true,
                                size: "lg",
                            }}
                        />
                    </div>
                </div>
            </section>

            <div className={styles.documentPreviewSection}>
                <FinancialDocumentPreview
                    financialDocument={{ id: documentId, fileName }}
                    compact
                    noPadding
                />
            </div>
        </div>
    );
};
